import { useQuery } from '@tanstack/react-query'
import { BookMarked, Search } from 'lucide-react'
import { useState } from 'react'
import { Pagination } from './MasterDataTable'
import { Badge, Swatch } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Field'
import { Card, EmptyState, LoadingPanel } from '@/components/ui/misc'
import { api, queryString } from '@/lib/api'
import { emptyDash, formatDate } from '@/lib/utils'

interface ReferenceRow {
  id: string
  code: string
  name: string
  status: string
  swatchHex: string | null
  referenceYear: number | null
  updatedAt: string
  manuscriptName: string | null
  ownerName: string | null
}

const PAGE_SIZE = 10

export function ReferenceList() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'references', { search, page }],
    queryFn: () =>
      api.get<{ entries: ReferenceRow[]; total: number }>(
        `/admin/references${queryString({ search, page, pageSize: PAGE_SIZE })}`,
      ),
  })

  const entries = data?.entries ?? []

  return (
    <>
      <header className="mb-4">
        <h2 className="font-display text-2xl font-semibold text-ink">References</h2>
        <p className="mt-0.5 text-[13px] text-ink-muted">
          Historical recipes transcribed from manuscripts. Created through the Reference form, not
          edited here.
        </p>
      </header>

      <div className="mb-4">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            setPage(1)
          }}
          placeholder="Search reference title or ID..."
          icon={<Search className="size-4" />}
        />
      </div>

      {isLoading ? (
        <Card>
          <LoadingPanel label="Loading references" />
        </Card>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={<BookMarked className="size-7" />}
          title="No references yet"
          description="References appear here once researchers transcribe recipes from the manuscript list."
        />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-paper-muted/60 text-left text-[12px] text-ink-muted">
                <th className="px-4 py-2.5 font-semibold">Reference ID</th>
                <th className="px-4 py-2.5 font-semibold">Title</th>
                <th className="px-4 py-2.5 font-semibold">Manuscript</th>
                <th className="px-4 py-2.5 font-semibold">Year</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Updated</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-b border-line last:border-0">
                  <td className="px-4 py-3 font-medium text-brand">{entry.code}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2 text-ink">
                      <Swatch hex={entry.swatchHex} size="xs" className="rounded-full" />
                      {entry.name || 'Untitled'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-muted">{emptyDash(entry.manuscriptName)}</td>
                  <td className="px-4 py-3 text-ink-muted">{emptyDash(entry.referenceYear)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={entry.status === 'published' ? 'positive' : 'neutral'}>
                      {entry.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-ink-muted">{formatDate(entry.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center justify-between border-t border-line px-4 py-2.5 text-[12px] text-ink-muted">
            <Pagination
              page={page}
              totalPages={Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE))}
              onChange={setPage}
            />
            <span>{data?.total ?? 0} references</span>
          </div>
        </Card>
      )}
    </>
  )
}
